import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Search, MapPin, Star, Mail, UserPlus, ChevronLeft, ChevronRight, Loader2, Send, Crown, Shield, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const PACKAGE_BADGES = { starter: { label:'Starter', cls:'bg-blue-100 text-blue-700', icon: Zap }, professional: { label:'Pro', cls:'bg-purple-100 text-purple-700', icon: Shield }, premium: { label:'Premium', cls:'bg-amber-100 text-amber-700', icon: Crown } };

export default function Directory() {
  const { user } = useAuth();
  const [professionals, setProfessionals] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [profession, setProfession] = useState('all');
  const [city, setCity] = useState('');
  const [professions, setProfessions] = useState([]);
  const [connectTarget, setConnectTarget] = useState(null);
  const [connectMsg, setConnectMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [credits, setCredits] = useState(null);
  const [sentIds, setSentIds] = useState(new Set());

  const load = useCallback(async (p=1) => {
    setLoading(true);
    try {
      const data = await api.getDirectory({ search, profession: profession === 'all' ? '' : profession, city, page: p });
      setProfessionals(data.professionals||[]);
      setTotal(data.total||0);
      setPages(data.pages||1);
      setPage(p);
    } catch { toast.error('Failed to load directory'); }
    finally { setLoading(false); }
  }, [search, profession, city]);

  useEffect(() => { load(1); }, [search, profession, city]); // eslint-disable-line
  useEffect(() => { api.getProfessions().then(d => setProfessions(d.professions||[])).catch(()=>{}); }, []);
  useEffect(() => { api.getConnectCredits().then(d => setCredits(d)).catch(()=>{}); }, []);

  const handleConnect = async () => {
    if (!connectMsg.trim()) return toast.error('Please write a message');
    setSending(true);
    try {
      const d = await api.sendConnectRequest(connectTarget.id, connectMsg);
      toast.success(d.message);
      setSentIds(prev => new Set([...prev, connectTarget.id]));
      setConnectTarget(null);
      setConnectMsg('');
      api.getConnectCredits().then(d => setCredits(d)).catch(()=>{});
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed to send request'); }
    finally { setSending(false); }
  };

  const openConnectDialog = (prof) => {
    setConnectTarget(prof);
    setConnectMsg(`Hi ${prof.full_name.split(' ')[0]}, I came across your profile and would love to connect. I'm a ${user?.profession_id?.replace(/_/g,' ')||'professional'} and I think there could be a great opportunity for us to collaborate.`);
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Outfit']">Professional Directory</h1>
            <p className="text-muted-foreground text-sm mt-0.5">{total.toLocaleString()} professionals — discover and connect</p>
          </div>
          {credits && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Connect Credits</p>
              <p className="text-sm font-semibold">
                {credits.credits_per_month >= 999 ? 'Unlimited' : `${credits.credits_remaining} / ${credits.credits_per_month}`}
                <span className="text-xs text-muted-foreground font-normal ml-1">this month</span>
              </p>
              <Link to="/connect" className="text-xs text-primary hover:underline">Upgrade plan</Link>
            </div>
          )}
        </div>

        {/* Search & Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search by name or company..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
          </div>
          <Select value={profession} onValueChange={setProfession}>
            <SelectTrigger className="w-48"><SelectValue placeholder="All Professions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Professions</SelectItem>
              {professions.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="City..." value={city} onChange={e => setCity(e.target.value)} className="w-36" />
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{[1,2,3,4,5,6,7,8].map(i=><div key={i} className="h-48 rounded-xl bg-muted animate-pulse"/>)}</div>
        ) : professionals.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-16 text-center text-muted-foreground">
            <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No professionals found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </CardContent></Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {professionals.map(prof => {
              const pkgBadge = PACKAGE_BADGES[prof.connect_package];
              const PkgIcon = pkgBadge?.icon;
              const alreadySent = sentIds.has(prof.id);
              return (
                <Card key={prof.id} className="hover:shadow-md transition-all hover:border-primary/20" data-testid={`dir-prof-${prof.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary text-lg font-bold overflow-hidden flex-shrink-0">
                        {prof.profile_photo ? <img src={resolveImageUrl(prof.profile_photo)} alt={prof.full_name} className="w-full h-full object-cover"/> : prof.full_name?.[0]}
                      </div>
                      {pkgBadge && (
                        <Badge className={`text-[10px] ${pkgBadge.cls}`}>
                          {PkgIcon && <PkgIcon className="w-2.5 h-2.5 mr-0.5" />}{pkgBadge.label}
                        </Badge>
                      )}
                    </div>
                    <Link to={`/card/${prof.id}`} className="font-semibold text-sm hover:text-primary transition-colors line-clamp-1">{prof.full_name}</Link>
                    <p className="text-xs text-primary/80 font-medium mt-0.5">{prof.profession}</p>
                    {prof.company && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{prof.company}</p>}
                    {prof.city && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{prof.city}{prof.state ? `, ${prof.state}`:''}</p>}
                    {prof.recommendation_count > 0 && (
                      <p className="text-xs text-amber-600 flex items-center gap-1 mt-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{prof.recommendation_count} recommendation{prof.recommendation_count!==1?'s':''}</p>
                    )}
                    <div className="flex gap-1.5 mt-3">
                      <Link to={`/card/${prof.id}`} className="flex-1">
                        <Button size="sm" variant="outline" className="w-full text-xs h-7">View Profile</Button>
                      </Link>
                      {prof.id !== user?.id && (
                        <Button size="sm" variant={alreadySent?'secondary':'default'} className="text-xs h-7 px-2" disabled={alreadySent}
                          onClick={() => !alreadySent && openConnectDialog(prof)} data-testid={`connect-btn-${prof.id}`}>
                          {alreadySent ? <Mail className="w-3.5 h-3.5"/> : <UserPlus className="w-3.5 h-3.5" />}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button size="sm" variant="outline" disabled={page<=1} onClick={() => load(page-1)}><ChevronLeft className="w-4 h-4"/></Button>
            <span className="text-sm text-muted-foreground">Page {page} of {pages}</span>
            <Button size="sm" variant="outline" disabled={page>=pages} onClick={() => load(page+1)}><ChevronRight className="w-4 h-4"/></Button>
          </div>
        )}
      </div>

      {/* Connect Dialog */}
      <Dialog open={!!connectTarget} onOpenChange={() => setConnectTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />Connect with {connectTarget?.full_name?.split(' ')[0]}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              <p className="font-medium">{connectTarget?.full_name}</p>
              <p className="text-muted-foreground text-xs">{connectTarget?.profession}{connectTarget?.company ? ` · ${connectTarget.company}` : ''}</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Personal message</label>
              <Textarea placeholder="Introduce yourself and explain why you'd like to connect..." value={connectMsg} onChange={e => setConnectMsg(e.target.value)} rows={5} />
            </div>
            {credits && credits.credits_per_month < 999 && (
              <p className="text-xs text-muted-foreground">{credits.credits_remaining} of {credits.credits_per_month} requests remaining this month · <Link to="/connect" className="text-primary hover:underline">Upgrade</Link></p>
            )}
            <p className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/20 p-2 rounded-md">
              <Send className="w-3.5 h-3.5 inline mr-1" />{connectTarget?.full_name} will receive an email with your message and profile link.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConnectTarget(null)}>Cancel</Button>
              <Button onClick={handleConnect} disabled={sending || !connectMsg.trim()} data-testid="send-connect-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Send className="w-4 h-4 mr-1" />}
                Send Connect Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
